import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { Box, Container, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';

import CardDetailDialog from '../cards/detail/CardDetailDialog';
import EditBoardDialog from './dialogs/EditBoardDialog';
import SetBackgroundDialog from './dialogs/SetBackgroundDialog';
import BoardDropzoneOverlay from './BoardDropzoneOverlay';
import AutomaticBoardCards from './layouts/AutomaticBoardCards';
import BoardTimer from './BoardTimer';
import BoardToolbar from './BoardToolbar';
import ColumnBoardCards from './layouts/ColumnBoardCards';
import CustomBoardCards from './layouts/CustomBoardCards';
import VoteAvailabilityBox from './voting/VoteAvailabilityBox';

import useBoardStore from '../../store/boardStore';
import useUserStore from '../../store/userStore';
import { resolveIdeaImageSrc } from '../../utils/ideaImage';
import { cacheImage } from '../../utils/imageCache';
import { getPublicAssetUrl } from '../../utils/publicAsset';
import { clearDotstormingWebAuthStorage } from '../../services/webAuth';
import { canAddCardsToBoardColumn } from '../../utils/boardColumns';

import type { BoardDetails, BoardTimerCompletionActions } from '../../interfaces/BoardDetails';

import { textComponentThemes } from '../../theme/textComponentThemes';
import { linkifyHtml } from '../../utils/linkifyHtml';
import { getCustomOrderedBoardIdeas, getOrderedBoardIdeas } from '../../utils/boardCardOrdering';
import type { RankBy } from '../../types/boardCardOrdering';

const hasRenderableDescription = (value: string) =>
  value
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim().length > 0;

const hasMarkup = (value: string) => /<\/?[a-z][\s\S]*>/i.test(value);

const formatPlainTextDescription = (value: string) => {
  if (typeof document === 'undefined') {
    return value.replace(/\r\n?/g, '\n').replace(/\n/g, '<br />');
  }

  const wrapper = document.createElement('div');
  wrapper.textContent = value;
  return wrapper.innerHTML.replace(/\r\n?/g, '\n').replace(/\n/g, '<br />');
};

const renderBoardDescription = (value: string) =>
  linkifyHtml(hasMarkup(value) ? value : formatPlainTextDescription(value));

const hasFilesInTransfer = (event: DragEvent<HTMLElement>) =>
  Array.from(event.dataTransfer?.types ?? []).includes('Files');

const inferUploadType = (file: File): 'image' | 'video' | 'pdf' | 'file' => {
  if (file.type.startsWith('image/')) {
    return 'image';
  }

  if (file.type.startsWith('video/')) {
    return 'video';
  }

  if (file.type === 'application/pdf') {
    return 'pdf';
  }

  if (/\.(png|jpe?g|gif|webp|bmp|svg|avif|heic|heif)$/i.test(file.name)) {
    return 'image';
  }

  if (/\.(mp4|mov|webm|m4v|avi|mpg|mpeg|wmv|flv|3gp|ogv|mkv)$/i.test(file.name)) {
    return 'video';
  }

  if (/\.pdf$/i.test(file.name)) {
    return 'pdf';
  }

  return 'file';
};

type BoardPageContentProps = {
  boardDetails: BoardDetails;
};

const BoardPageContent = ({ boardDetails }: BoardPageContentProps) => {
  const ideas = useBoardStore((state) => state.ideas);
  const addIdeaImage = useBoardStore((state) => state.addIdeaImage);
  const addIdeaVideo = useBoardStore((state) => state.addIdeaVideo);
  const addIdeaFile = useBoardStore((state) => state.addIdeaFile);
  const reorderIdeaSortOrder = useBoardStore((state) => state.reorderIdeaSortOrder);
  const moveIdeaToColumn = useBoardStore((state) => state.moveIdeaToColumn);
  const getTotalVotesByOwner = useBoardStore((state) => state.getTotalVotesByOwner);
  const setPrivateVotingEnabled = useBoardStore((state) => state.setPrivateVotingEnabled);
  const setVotingEnabled = useBoardStore((state) => state.setVotingEnabled);
  const setCommentingEnabled = useBoardStore((state) => state.setCommentingEnabled);
  const setChatEnabled = useBoardStore((state) => state.setChatEnabled);
  const setParticipantsEnabled = useBoardStore((state) => state.setParticipantsEnabled);
  const updateBoardDetails = useBoardStore((state) => state.updateBoardDetails);
  const updateBoardColumn = useBoardStore((state) => state.updateBoardColumn);
  const setGiphyEnabled = useBoardStore((state) => state.setGiphyEnabled);
  const setShowVoterNames = useBoardStore((state) => state.setShowVoterNames);
  const showVoteOwners = useBoardStore((state) => state.showVoteOwners);
  const setParticipantNameVisibility = useBoardStore((state) => state.setParticipantNameVisibility);
  const setBoardLocked = useBoardStore((state) => state.setBoardLocked);
  const startBoardTimer = useBoardStore((state) => state.startBoardTimer);
  const stopBoardTimer = useBoardStore((state) => state.stopBoardTimer);
  const resumeBoardTimer = useBoardStore((state) => state.resumeBoardTimer);
  const resetBoardTimer = useBoardStore((state) => state.resetBoardTimer);
  const completeBoardTimer = useBoardStore((state) => state.completeBoardTimer);
  const getBoardBackground = useBoardStore((state) => state.getBoardBackground);
  const setBoardBackground = useBoardStore((state) => state.setBoardBackground);
  const exportBoard = useBoardStore((state) => state.exportBoard);
  const saveBoardTemplate = useBoardStore((state) => state.saveBoardTemplate);
  const getBoardQrCode = useBoardStore((state) => state.getBoardQrCode);
  const sendInvitations = useBoardStore((state) => state.sendInvitations);
  const sendFeedback = useBoardStore((state) => state.sendFeedback);
  const clearBoard = useBoardStore((state) => state.clearBoard);
  const participants = useBoardStore((state) => state.participants);
  const chat = useBoardStore((state) => state.chat);
  const currentUser = useUserStore((state) => state.getCurrentUser());
  const setCurrentUser = useUserStore((state) => state.setCurrentUser);
  const isCustomCardSorting = boardDetails.cardSorting === 'custom';
  const defaultRankBy: RankBy =
    isCustomCardSorting || boardDetails.randomizeCardPosition === true ? 'board' : 'added';
  const [rankBy, setRankBy] = useState<RankBy>(defaultRankBy);
  const [isEditBoardDialogOpen, setIsEditBoardDialogOpen] = useState(false);
  const [isSetBackgroundDialogOpen, setIsSetBackgroundDialogOpen] = useState(false);
  const [backgroundUrl, setBackgroundUrl] = useState<string | null>(null);
  const dragDepthRef = useRef(0);
  const gridRandomOrderRef = useRef(new Map<string, number>());
  const columnRandomOrderRef = useRef(new Map<string, number>());
  const [isBoardDropActive, setIsBoardDropActive] = useState(false);
  const [isDropUploading, setIsDropUploading] = useState(false);
  const [dropUploadCount, setDropUploadCount] = useState(0);
  const previousBoardIdRef = useRef(boardDetails._id);
  const previousRandomizeCardPositionRef = useRef(boardDetails.randomizeCardPosition === true);
  const layout = boardDetails.layout ?? 'lanes';
  const enabledUploadTypes = useMemo(
    () =>
      new Set(
        (boardDetails.uploadTypes ?? '')
          .split(',')
          .map((uploadType) => uploadType.trim())
          .filter(Boolean)
      ),
    [boardDetails.uploadTypes]
  );
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));

  const laneCount = isLgUp ? 4 : isSmUp ? 4 : 1;
  const boardColumns = boardDetails.columns ?? [];
  const hasBoardDescription = hasRenderableDescription(boardDetails.description);

  const isDotVotingStyle = boardDetails.votingStyle === 'dotvoting';
  const hasVoteSorting = boardDetails.votingStyle !== 'none';
  const isBoardLocked = boardDetails.locked === true;
  const canCurrentUserAddCards = boardDetails.participantUploadsEnabled !== false;
  const isCustomSortDragEnabled = isCustomCardSorting && boardDetails.isOwner && !isBoardLocked;
  const votingEnabled = boardDetails.votingEnabled ?? true;
  const commentsEnabled = boardDetails.commentsEnabled !== false;
  const privateVotingEnabled = boardDetails.privateVotingEnabled === true;
  const canUseRankingControls =
    !isCustomCardSorting && !(privateVotingEnabled && !boardDetails.isOwner);
  const usedVotes = currentUser && isDotVotingStyle ? getTotalVotesByOwner(currentUser.id) : 0;
  const voteLimit = isDotVotingStyle ? boardDetails.votesPerUser ?? 0 : 0;
  const availableVotes = Math.max(0, voteLimit - usedVotes);
  const workspaceLinkHref =
    boardDetails.includeWorkspaceLink && boardDetails.workspaceExternalId?.trim()
      ? `https://workspace.dotstorming.com/${boardDetails.workspaceExternalId.trim()}`
      : null;
  const workspaceLinkLabel = boardDetails.workspaceName?.trim() ?? '';
  const mainAppHref = boardDetails.workspace?.trim()
    ? `/main?workspaceId=${boardDetails.workspace.trim()}`
    : '/main';

  const updateBackgroundUrl = useCallback((nextBackgroundUrl: string | null) => {
    setBackgroundUrl((currentBackgroundUrl) => {
      if (
        currentBackgroundUrl &&
        currentBackgroundUrl !== nextBackgroundUrl &&
        currentBackgroundUrl.startsWith('blob:')
      ) {
        window.URL.revokeObjectURL(currentBackgroundUrl);
      }

      return nextBackgroundUrl;
    });
  }, []);

  const handleSetParticipantNameVisibility = (mode: 'hidden' | 'owner-only' | 'everyone') => {
    void setParticipantNameVisibility(mode);
  };

  const handleToggleShowVoteOwners = () => {
    setShowVoterNames(!showVoteOwners);
  };

  const handleTogglePrivateVoting = () => {
    if (!isDotVotingStyle) {
      return;
    }
    void setPrivateVotingEnabled(!(boardDetails.privateVotingEnabled ?? false));
  };

  const handleToggleLockBoard = () => {
    void setBoardLocked(!(boardDetails.locked ?? false));
  };

  const handleToggleVoting = () => {
    if (!isDotVotingStyle) {
      return;
    }
    void setVotingEnabled(!(boardDetails.votingEnabled ?? true));
  };

  const handleToggleCommenting = () => {
    void setCommentingEnabled(!(boardDetails.commentsEnabled ?? true));
  };

  const handleToggleChat = () => {
    void setChatEnabled(!(boardDetails.chatEnabled ?? true));
  };

  const handleToggleTimer = () => {
    void updateBoardDetails({
      timerEnabled: !(boardDetails.timerEnabled ?? true),
    }).catch(() => undefined);
  };

  const handleToggleParticipants = () => {
    void setParticipantsEnabled(!(boardDetails.participantsEnabled ?? true));
  };

  const handleToggleParticipantUploads = () => {
    void updateBoardDetails({
      participantUploadsEnabled: !(boardDetails.participantUploadsEnabled ?? true),
    }).catch(() => undefined);
  };

  const handleToggleGifs = () => {
    void setGiphyEnabled(!(boardDetails.giphyEnabled ?? true));
  };

  const handleStartBoardTimer = async (
    durationSeconds: number,
    completionActions: BoardTimerCompletionActions
  ) => {
    await startBoardTimer(durationSeconds, completionActions);
  };

  const handleCompleteBoardTimer = useCallback(async () => {
    await completeBoardTimer();
  }, [completeBoardTimer]);

  const handleLogout = () => {
    clearDotstormingWebAuthStorage();
    useUserStore.getState().clearAllBoardSessions();
    setCurrentUser(null);
  };

  const handleSaveBackground = async (file: File, repeat: boolean) => {
    const didSave = await setBoardBackground(file, repeat);
    if (!didSave) {
      return false;
    }

    const nextBackgroundUrl = await getBoardBackground();
    updateBackgroundUrl(nextBackgroundUrl);

    return true;
  };

  useEffect(() => {
    if (!commentsEnabled && rankBy === 'comments') {
      setRankBy(defaultRankBy);
    }
  }, [commentsEnabled, defaultRankBy, rankBy]);

  useEffect(() => {
    if (!hasVoteSorting && rankBy === 'votes') {
      setRankBy(defaultRankBy);
    }
  }, [defaultRankBy, hasVoteSorting, rankBy]);

  useEffect(() => {
    if (!canUseRankingControls && rankBy !== defaultRankBy) {
      setRankBy(defaultRankBy);
    }
  }, [canUseRankingControls, defaultRankBy, rankBy]);

  useEffect(() => {
    if (previousBoardIdRef.current === boardDetails._id) {
      return;
    }

    previousBoardIdRef.current = boardDetails._id;
    gridRandomOrderRef.current.clear();
    columnRandomOrderRef.current.clear();
    previousRandomizeCardPositionRef.current = boardDetails.randomizeCardPosition === true;
    setRankBy(defaultRankBy);
  }, [boardDetails._id, boardDetails.randomizeCardPosition, defaultRankBy]);

  useEffect(() => {
    const isRandomized = boardDetails.randomizeCardPosition === true;

    if (!previousRandomizeCardPositionRef.current && isRandomized) {
      gridRandomOrderRef.current.clear();
      columnRandomOrderRef.current.clear();
    }

    previousRandomizeCardPositionRef.current = isRandomized;
  }, [boardDetails.randomizeCardPosition]);

  useEffect(() => {
    if (rankBy === 'board' && defaultRankBy !== 'board') {
      setRankBy(defaultRankBy);
    }
  }, [defaultRankBy, rankBy]);

  useEffect(() => {
    if (!boardDetails.background?.image?.trim()) {
      updateBackgroundUrl(null);
      return;
    }

    let isCancelled = false;

    void (async () => {
      const nextBackgroundUrl = await getBoardBackground();

      if (isCancelled) {
        if (nextBackgroundUrl?.startsWith('blob:')) {
          window.URL.revokeObjectURL(nextBackgroundUrl);
        }
        return;
      }

      updateBackgroundUrl(nextBackgroundUrl);
    })();

    return () => {
      isCancelled = true;
    };
  }, [boardDetails.background?.image, getBoardBackground, updateBackgroundUrl]);

  useEffect(
    () => () => {
      if (backgroundUrl?.startsWith('blob:')) {
        window.URL.revokeObjectURL(backgroundUrl);
      }
    },
    [backgroundUrl]
  );

  const visibleIdeas = useMemo(() => {
    if (boardDetails.cardsRequireApproval !== true) {
      return ideas;
    }

    return ideas.filter((idea) => {
      if (idea.approved !== false) {
        return true;
      }

      if (boardDetails.isOwner) {
        return true;
      }

      if (currentUser && idea.ownerId === currentUser.id) {
        return true;
      }

      return false;
    });
  }, [boardDetails.cardsRequireApproval, boardDetails.isOwner, currentUser, ideas]);

  const orderedBoardIdeas = useMemo(() => {
    if (isCustomCardSorting) {
      return getCustomOrderedBoardIdeas(visibleIdeas);
    }

    return getOrderedBoardIdeas(
      visibleIdeas,
      rankBy,
      boardDetails.randomizeCardPosition === true,
      gridRandomOrderRef
    );
  }, [
    boardDetails.randomizeCardPosition,
    gridRandomOrderRef,
    isCustomCardSorting,
    rankBy,
    visibleIdeas,
  ]);

  useEffect(() => {
    const imageIdeaSources = visibleIdeas
      .filter((idea) => idea.type === 'image')
      .map((idea) => resolveIdeaImageSrc(idea));

    imageIdeaSources.forEach((source) => {
      void cacheImage(source);
    });
  }, [visibleIdeas]);

  const handleBoardDragEnter = (event: DragEvent<HTMLElement>) => {
    if (!hasFilesInTransfer(event) || !currentUser || isBoardLocked || !canCurrentUserAddCards) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current += 1;
    setIsBoardDropActive(true);
  };

  const handleBoardDragOver = (event: DragEvent<HTMLElement>) => {
    if (!hasFilesInTransfer(event) || !currentUser || isBoardLocked || !canCurrentUserAddCards) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleBoardDragLeave = (event: DragEvent<HTMLElement>) => {
    if (!hasFilesInTransfer(event) || !currentUser || isBoardLocked || !canCurrentUserAddCards) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    dragDepthRef.current = Math.max(dragDepthRef.current - 1, 0);
    if (dragDepthRef.current === 0) {
      setIsBoardDropActive(false);
    }
  };

  const uploadDroppedFiles = async (files: File[], options?: { columnId?: string }) => {
    if (!currentUser || isBoardLocked || !canCurrentUserAddCards) {
      return;
    }

    if (files.length === 0) {
      return;
    }

    const uploadActions = files
      .map((file) => {
        const uploadType = inferUploadType(file);

        if (!enabledUploadTypes.has(uploadType)) {
          return null;
        }

        if (uploadType === 'image') {
          return () => addIdeaImage(file, undefined, options);
        }

        if (uploadType === 'video') {
          return () => addIdeaVideo(file, file.name, options);
        }

        if (uploadType === 'pdf') {
          return () => addIdeaFile(file, file.name, options);
        }

        return () => addIdeaFile(file, file.name, options);
      })
      .filter(Boolean) as Array<() => Promise<void>>;

    if (uploadActions.length === 0) {
      return;
    }

    setIsDropUploading(true);
    setDropUploadCount(uploadActions.length);

    try {
      await Promise.all(uploadActions.map((action) => action()));
    } finally {
      setIsDropUploading(false);
      setDropUploadCount(0);
    }
  };

  const handleBoardDrop = async (event: DragEvent<HTMLElement>) => {
    if (!hasFilesInTransfer(event) || isBoardLocked || !canCurrentUserAddCards) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    dragDepthRef.current = 0;
    setIsBoardDropActive(false);

    await uploadDroppedFiles(Array.from(event.dataTransfer.files ?? []));
  };

  const handleColumnFileDrop = async (files: File[], columnId: string) => {
    if (
      !canAddCardsToBoardColumn(boardColumns, columnId, !isBoardLocked && canCurrentUserAddCards)
    ) {
      return;
    }

    dragDepthRef.current = 0;
    setIsBoardDropActive(false);

    await uploadDroppedFiles(files, { columnId });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f9faf9',
        ...(backgroundUrl
          ? {
              backgroundImage: `url(${backgroundUrl})`,
              backgroundRepeat: boardDetails.background?.repeat ? 'repeat' : 'no-repeat',
              backgroundPosition: boardDetails.background?.repeat ? 'top left' : 'center center',
              backgroundSize: boardDetails.background?.repeat ? 'auto' : 'cover',
              backgroundAttachment: boardDetails.background?.repeat ? 'scroll' : 'fixed',
            }
          : {}),
      }}
      onDragEnter={handleBoardDragEnter}
      onDragOver={handleBoardDragOver}
      onDragLeave={handleBoardDragLeave}
      onDrop={(event) => void handleBoardDrop(event)}
    >
      <BoardDropzoneOverlay
        isActive={isBoardDropActive}
        isUploading={isDropUploading}
        uploadCount={dropUploadCount}
      />
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          zIndex: 1000,
          maxWidth: 'calc(100vw - 32px)',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            borderRadius: 3,
            px: 3,
            height: 52,
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            maxWidth: '100%',
          }}
        >
          {workspaceLinkHref && workspaceLinkLabel ? (
            <Stack direction="row" spacing={2} gap={1} alignItems="center" sx={{ minWidth: 0 }}>
              <Box
                component="a"
                href={workspaceLinkHref}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#04ceff',
                  textDecoration: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {workspaceLinkLabel}
              </Box>
              <Typography
                variant="h4"
                sx={{
                  m: 0,
                  p: 0,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {boardDetails.title}
              </Typography>
            </Stack>
          ) : boardDetails.logoVisible !== false ? (
            <Stack direction="row" spacing={2} gap={1} alignItems="center" sx={{ minWidth: 0 }}>
              <Box
                component="a"
                href={mainAppHref}
                aria-label="Open Dotstorming"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                <Box
                  component="img"
                  src={getPublicAssetUrl('/img/logo_small.svg')}
                  alt="Dotstorming"
                  sx={{
                    display: 'block',
                    height: 30,
                  }}
                />
              </Box>
              <Typography
                variant="h4"
                sx={{
                  m: 0,
                  p: 0,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {boardDetails.title}
              </Typography>
            </Stack>
          ) : (
            <Typography
              variant="h4"
              sx={{
                m: 0,
                p: 0,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {boardDetails.title}
            </Typography>
          )}
        </Paper>
        <BoardTimer
          boardDetails={boardDetails}
          onStop={stopBoardTimer}
          onResume={resumeBoardTimer}
          onReset={resetBoardTimer}
          onComplete={handleCompleteBoardTimer}
        />
      </Stack>
      {!isBoardLocked && votingEnabled && isDotVotingStyle && Boolean(currentUser) && (
        <Box
          sx={{
            backgroundColor: '#04ceff',
            position: 'fixed',
            top: 16,
            right: 16,
            borderRadius: 3,
            px: 2.5,
            height: 52,
            zIndex: 1000,
            display: { xs: 'flex', sm: 'none' },
            alignItems: 'center',
            boxShadow:
              '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
          }}
        >
          <VoteAvailabilityBox
            availableVotes={availableVotes}
            usedVotes={usedVotes}
            voteLimit={voteLimit}
            textColor="#ffffff"
          />
        </Box>
      )}
      <BoardToolbar
        isLocked={isBoardLocked}
        isDotVotingStyle={isDotVotingStyle}
        votingEnabled={votingEnabled}
        hasCurrentUser={Boolean(currentUser)}
        availableVotes={availableVotes}
        usedVotes={usedVotes}
        voteLimit={voteLimit}
        chatEnabled={boardDetails.chatEnabled !== false}
        commentsEnabled={commentsEnabled}
        canUseRankingControls={canUseRankingControls}
        hasVoteSorting={hasVoteSorting}
        boardDetails={boardDetails}
        participantsEnabled={boardDetails.participantsEnabled !== false}
        participantUploadsEnabled={boardDetails.participantUploadsEnabled !== false}
        isOwner={boardDetails.isOwner}
        canAddCards={layout !== 'columns' && canCurrentUserAddCards}
        randomizeCardPosition={boardDetails.randomizeCardPosition === true}
        chat={chat}
        participants={participants}
        rankBy={rankBy}
        onRankByChange={setRankBy}
        viewParticipantNames={boardDetails.viewParticipantNames === true}
        allViewParticipantNames={boardDetails.allViewParticipantNames === true}
        showVoterNames={showVoteOwners}
        privateVotingEnabled={boardDetails.privateVotingEnabled === true}
        giphyEnabled={boardDetails.giphyEnabled !== false}
        onSetParticipantNameVisibility={handleSetParticipantNameVisibility}
        onToggleShowVoteOwners={handleToggleShowVoteOwners}
        onTogglePrivateVoting={handleTogglePrivateVoting}
        onToggleLockBoard={handleToggleLockBoard}
        onToggleVoting={handleToggleVoting}
        onToggleCommenting={handleToggleCommenting}
        onToggleChat={handleToggleChat}
        onToggleTimer={handleToggleTimer}
        onToggleParticipants={handleToggleParticipants}
        onToggleParticipantUploads={handleToggleParticipantUploads}
        onToggleGifs={handleToggleGifs}
        onExportBoard={exportBoard}
        onSaveBoardTemplate={saveBoardTemplate}
        onLoadQrCode={getBoardQrCode}
        onSendInvitations={sendInvitations}
        onSendFeedback={sendFeedback}
        onClearBoard={clearBoard}
        onStartTimer={handleStartBoardTimer}
        onLogout={handleLogout}
        onOpenSetBackground={() => setIsSetBackgroundDialogOpen(true)}
        onOpenSettings={() => setIsEditBoardDialogOpen(true)}
      />
      <Container
        sx={{
          pt: 11,
          pb: { xs: 12, sm: 2 },
          ...(layout === 'columns'
            ? {
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                height: { xs: 'auto', md: '100vh' },
                overflow: { xs: 'visible', md: 'hidden' },
              }
            : {}),
        }}
      >
        {hasBoardDescription && (
          <Box sx={{ px: 2 }}>
            <Box
              color={textComponentThemes.board_description.textColor}
              bgcolor={textComponentThemes.board_description.backgroundColor}
              fontWeight={textComponentThemes.board_description.fontWeight}
              p={2}
              borderRadius={2}
              sx={{
                fontSize: '15px',
                fontWeight: 700,
                '& p': { m: 0 },
                '& p + p': { mt: 1.25 },
                '& a': { color: 'inherit' },
              }}
              dangerouslySetInnerHTML={{ __html: renderBoardDescription(boardDetails.description) }}
            />
          </Box>
        )}
        <Box
          sx={{
            px: 2,
            pt: hasBoardDescription ? 2 : 0,
            mb: layout === 'columns' ? 0 : 3,
            ...(layout === 'columns'
              ? {
                  flex: { md: 1 },
                  minHeight: { md: 0 },
                  overflow: { xs: 'visible', md: 'hidden' },
                }
              : {}),
          }}
        >
          {layout === 'columns' ? (
            <ColumnBoardCards
              ideas={visibleIdeas}
              columns={boardColumns}
              rankBy={rankBy}
              randomizeCardPosition={boardDetails.randomizeCardPosition === true}
              randomOrderRef={columnRandomOrderRef}
              moveEnabled={boardDetails.isOwner && !isBoardLocked}
              canAddCards={!isBoardLocked && canCurrentUserAddCards}
              canEditColumns={boardDetails.isOwner && !isBoardLocked}
              onMoveIdea={moveIdeaToColumn}
              onReorder={reorderIdeaSortOrder}
              onUpdateColumn={updateBoardColumn}
              onDropFilesToColumn={handleColumnFileDrop}
              customSortEnabled={isCustomSortDragEnabled}
            />
          ) : isCustomCardSorting ? (
            <CustomBoardCards
              orderedIdeas={orderedBoardIdeas}
              layout={layout}
              laneCount={laneCount}
              dragEnabled={isCustomSortDragEnabled}
              onReorder={reorderIdeaSortOrder}
            />
          ) : (
            <AutomaticBoardCards
              orderedIdeas={orderedBoardIdeas}
              layout={layout}
              laneCount={laneCount}
            />
          )}
        </Box>
      </Container>
      <EditBoardDialog
        open={isEditBoardDialogOpen}
        onClose={() => setIsEditBoardDialogOpen(false)}
      />
      <SetBackgroundDialog
        open={isSetBackgroundDialogOpen}
        initialRepeat={boardDetails.background?.repeat === true}
        onClose={() => setIsSetBackgroundDialogOpen(false)}
        onSave={handleSaveBackground}
      />
      <CardDetailDialog
        rankBy={rankBy}
        orderedBoardIdeas={orderedBoardIdeas}
        columnRandomOrderRef={columnRandomOrderRef}
      />
    </Box>
  );
};

export default BoardPageContent;
